import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import GQL from '@lib/cms/gql';
import FlexibleDataOptimized from '@lib/cms/flexibleDataOptimized';
import { ALL_BUILDER_DEVELOPMENT_QUERY } from '@lib/fragments';

import Container from '@layout/container';
import Grid from '@layout/grid';
import FlexiblePage from '@templates/FlexiblePage';
import Select from '@components/select';
import ArticleCard from '@components/article-card';

import { search_bar, state_sort, search, card_outer, zero_state } from './styles.module.scss';

export async function getStaticProps(context) {
  const preview = context.preview || false;
  const previewEnv = context?.previewData?.environment ?? null;
  const landingData = await FlexibleDataOptimized.fetch('development', preview, previewEnv);

  const developmentData = await GQL.fetch(
    ALL_BUILDER_DEVELOPMENT_QUERY,
    { category: 'Development' },
    { preview },
    preview,
    previewEnv
  );

  const allDevelopments = developmentData?.data?.pageBuilderdevelopmentCollection?.items ?? [];

  // Create service area object with respective counts of developments
  const serviceAreas = {};
  allDevelopments.forEach((dev) => {
    serviceAreas[dev.serviceArea] = (serviceAreas[dev.serviceArea] || 0) + 1;
  });
  // Coerce to array, sort by count, create label/value object
  const serviceAreaOptions = Object.entries(serviceAreas)
    .sort(([, a], [, b]) => b - a)
    .map(([el]) => {
      return { label: el, value: el };
    });

  return {
    props: { allDevelopments, landingData, preview, serviceAreaOptions }
  };
}

const DevelopmentLandingPage = ({ allDevelopments, landingData, serviceAreaOptions, preview }) => {
  const router = useRouter();
  const topRef = useRef();
  const [areaValue, setAreaValue] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter Developments by service Area & 'starts with' input text
  const filteredDevelopments = allDevelopments.filter((dev) => {
    const serviceAreaMatch = areaValue === 'all' || areaValue === dev.serviceArea;

    // Searching by builderDevelopmentName and Nearby Community titles
    const searchTermMatch =
      dev?.builderDevelopmentName?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      dev?.nearbyCommunities?.items?.filter((com) =>
        com?.title?.toLowerCase().includes(searchTerm?.toLowerCase())
      ).length > 0;

    return serviceAreaMatch && searchTermMatch;
  });

  useEffect(() => {
    if (router?.isReady) {
      const area = router?.query?.area;
      setAreaValue(area || 'all');
    }

    return () => {
      setAreaValue();
    };
  }, [router]);

  const handleSelectChange = (newAreaValue) => {
    setAreaValue(newAreaValue);

    router.push({ pathname: router.pathname, query: { area: newAreaValue } }, undefined, {
      shallow: true
    });
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);

    router.push({ pathname: router.pathname, query: { ...router.query } }, undefined, {
      shallow: true
    });
  };

  return (
    <FlexiblePage data={landingData} preview={preview}>
      {landingData && (
        <main>
          <Container spacing="large" gridOptions={{ layout: 'single' }}>
            <div ref={topRef} className={search_bar}>
              <div className={state_sort}>
                <Select
                  options={[{ label: 'All', value: 'all' }, ...serviceAreaOptions]}
                  onChange={handleSelectChange}
                  selected={areaValue}
                />
              </div>
              <div className={search}>
                <input
                  tabIndex="0"
                  value={searchTerm}
                  onChange={handleInputChange}
                  placeholder="Search by Development Name"
                />
              </div>
            </div>

            {filteredDevelopments.length > 0 ? (
              <Grid gutter="20" layout="3">
                {filteredDevelopments.map((development) => {
                  const getEyebrowText = () => {
                    if (development?.nearbyCommunities?.items[0]?.title) {
                      return development?.nearbyCommunities?.items[0]?.title.toUpperCase();
                    } else {
                      return development.serviceArea.toUpperCase();
                    }
                  };

                  return (
                    <div className={card_outer} key={development?.slug}>
                      <ArticleCard
                        description={development?.excerpt}
                        eyebrow={getEyebrowText()}
                        image={development?.featuredImage}
                        link={{
                          aria: 'Explore this development',
                          icon: true,
                          label: 'Explore',
                          url: `/development/${development?.slug}`
                        }}
                        theme="community"
                        title={development?.builderDevelopmentName}
                      />
                    </div>
                  );
                })}
              </Grid>
            ) : (
              <div className={zero_state}>
                {areaValue !== 'all'
                  ? 'No Developments available under this filter. Try selecting another.'
                  : 'No Developments meet your search criteria .'}
              </div>
            )}
          </Container>
        </main>
      )}
    </FlexiblePage>
  );
};

export default DevelopmentLandingPage;
